import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { getStandardsFromCDN } from '../services/SkillService';
import { DiffitUserContext } from '../contexts/DiffitUserContext';
import { isPremiumUser } from '../utils/isPremiumUser';
import { useUser } from '@clerk/react';
import {
  SUBJECT_MAP,
  STATE_STANDARD_SETS,
  getStandardSet,
  getSubject,
  getSubjectForQuestions,
  skillsStorage,
  getGradeLevelKey,
  determineEffectiveSubjectKey,
  requiresStateSelection,
  constrainSubjectForQuestions,
  getDefaultCourseKey,
  getSubjectCourses,
  resolveSubjectPath,
  resolveGradeToken,
} from '../utils/skills';

/**
 * Default comprehension skill object used for free users
 */
const COMPREHENSION_SKILL = {
  description:
    'Depth of Knowledge Level One - Understanding and recalling information or ideas presented.',
  framework: 'Depth of Knowledge',
  id: 'DOK.COMPREHENSION',
  name: 'DOK.COMPREHENSION',
};

/**
 * Custom hook for managing skills selection state and logic
 * @param {string} mode - 'packetSkills' or 'addQuestion'
 * @param {Object} initialFilters - Optional initial filter values
 * @param {Array} initialLanguageStandards - Optional initial selected language standards
 * @param {Object} initialContentStandard - Optional initial selected content standard
 */
export const useSkillsSelection = (
  mode = 'packetSkills',
  initialFilters = {},
  initialLanguageStandards = [],
  initialContentStandard = null
) => {
  const { diffitUser } = useContext(DiffitUserContext);
  const { user } = useUser();
  const hasPremium = isPremiumUser(user, diffitUser);

  // Track which skill IDs were initially selected (these always stay at top when selected)
  const [initiallySelectedIds] = useState(() => {
    const languageIds = initialLanguageStandards.map((skill) => skill?.id).filter(Boolean);
    const contentId = initialContentStandard?.id;
    return new Set([...languageIds, ...(contentId ? [contentId] : [])]);
  });

  // Initialize filters based on mode and initial values
  const [filters, setFilters] = useState(() => {
    // Question type is the actual type of question (mcq, saq, oeq)
    const questionType = mode === 'addQuestion' ? initialFilters.questionType : null;

    // Subject for questions is the subject/standard framework to use for generating questions
    const subjectForQuestionsKey =
      mode === 'addQuestion'
        ? initialFilters.subjectForQuestionsKey || getSubjectForQuestions()
        : null;

    const selectedSubjectKey =
      mode === 'addQuestion'
        ? subjectForQuestionsKey || 'DOK'
        : getSubject() || 'ENGLISH_LANGUAGE_ARTS';

    // Initialize grade directly with the key
    const gradeKey = getGradeLevelKey(diffitUser);

    // Standard set initialization
    const getInitialStandardSet = () => {
      // In addQuestion mode, only use override if the selected subject for questions requires it
      const subjectToCheck = mode === 'addQuestion' ? subjectForQuestionsKey : selectedSubjectKey;
      if (subjectToCheck && SUBJECT_MAP[subjectToCheck]?.setOverride) {
        return SUBJECT_MAP[subjectToCheck].setOverride;
      }
      // Otherwise use the user's saved standard set (their state)
      return getStandardSet(diffitUser) || '';
    };

    return {
      subjectKey: initialFilters.subjectKey || selectedSubjectKey,
      courseKey:
        initialFilters.courseKey ||
        getDefaultCourseKey(initialFilters.subjectKey || selectedSubjectKey),
      gradeKey: initialFilters.gradeKey || gradeKey,
      standardSetKey: initialFilters.standardSetKey || getInitialStandardSet(),
      questionType, // The actual question type (mcq, saq, oeq)
      subjectForQuestionsKey, // The subject/framework for generating questions
      searchQuery: '',
    };
  });

  // Initialize selected language standards
  const getInitialLanguageStandards = () => {
    if (mode === 'packetSkills') {
      return initialLanguageStandards.filter((skill) => skill && skill.id);
    }
    // Free users in question mode get comprehension skill by default
    if (mode === 'addQuestion' && !hasPremium) {
      return [COMPREHENSION_SKILL];
    }
    return [];
  };

  // Initialize selected content standards (now supports multiple)
  const getInitialContentStandards = () => {
    if (mode === 'packetSkills') {
      // Support both single object (legacy) and array formats
      if (Array.isArray(initialContentStandard)) {
        return initialContentStandard.filter((s) => s && s.id);
      }
      return initialContentStandard && initialContentStandard.id ? [initialContentStandard] : [];
    }
    return [];
  };

  // Store language and content standards separately (both as arrays)
  const [selectedLanguageStandards, setSelectedLanguageStandards] = useState(
    getInitialLanguageStandards
  );
  const [selectedContentStandards, setSelectedContentStandards] = useState(
    getInitialContentStandards
  );

  // Derive IDs for efficient lookups
  const selectedSkillIds = [
    ...selectedLanguageStandards.map((skill) => skill.id),
    ...selectedContentStandards.map((skill) => skill.id),
  ];

  const [skills, setSkills] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const lastRequestId = useRef(0);

  // Fetch standards from CDN
  const fetchStandards = useCallback(async () => {
    const { standardSetKey, gradeKey, courseKey, subjectForQuestionsKey } = filters;

    // Don't fetch for custom question type
    if (mode === 'addQuestion' && subjectForQuestionsKey === 'CUSTOM') {
      setSkills([]); // Clear skills for custom questions
      return [];
    }

    // In addQuestion mode, use subjectForQuestionsKey as the subject key for fetching
    const effectiveSubjectKey = determineEffectiveSubjectKey(filters, mode);

    // Don't fetch if no subject is selected
    if (!effectiveSubjectKey) {
      setSkills([]); // Clear skills when no subject
      return [];
    }

    // Wait for state selection unless subject has an override
    if (requiresStateSelection(effectiveSubjectKey, standardSetKey)) {
      setSkills([]);
      return [];
    }

    const requestId = ++lastRequestId.current;
    setIsLoading(true);
    setError(null);

    try {
      const subjectPath = resolveSubjectPath(effectiveSubjectKey, courseKey);
      const gradeToken = resolveGradeToken(effectiveSubjectKey, gradeKey);

      // Use override set if subject specifies one, otherwise use selected standard set
      let setKey = SUBJECT_MAP[effectiveSubjectKey]?.setOverride || standardSetKey;

      if (!setKey || !subjectPath || !gradeToken) {
        if (requestId === lastRequestId.current) {
          setIsLoading(false);
          setSkills([]); // Clear the skills array when no standards are available
        }
        return [];
      }

      const response = await getStandardsFromCDN(setKey, subjectPath, gradeToken);
      const data = Array.isArray(response.data) ? response.data : [];
      if (requestId === lastRequestId.current) {
        if (!SUBJECT_MAP[effectiveSubjectKey]?.skipSorting) {
          data.sort((a, b) => {
            const aCCRA = a.strand.indexOf('College and Career Readiness') !== -1;
            const bCCRA = b.strand.indexOf('College and Career Readiness') !== -1;
            // put CCRA skills to the bottom
            if (aCCRA && !bCCRA) return 1;
            if (!aCCRA && bCCRA) return -1;

            // Otherwise, sort by id
            return a.id.localeCompare(b.id, undefined, { numeric: true });
          });
        }
        setSkills(data);
        setIsLoading(false);
      }
      return data;
    } catch (err) {
      console.error('Error fetching standards:', err);
      if (requestId === lastRequestId.current) {
        setError(err);
        setSkills([]); // Clear skills on error
        setIsLoading(false);
      }
      return [];
    }
  }, [
    filters.subjectKey,
    filters.standardSetKey,
    filters.gradeKey,
    filters.courseKey,
    filters.subjectForQuestionsKey,
    mode,
  ]);

  // Fetch standards when filters change
  useEffect(() => {
    fetchStandards();
  }, [fetchStandards]);

  // Point at a valid course whenever the subject changes (course-based subjects only)
  useEffect(() => {
    const effectiveSubjectKey = determineEffectiveSubjectKey(filters, mode);
    const courses = getSubjectCourses(effectiveSubjectKey);
    const nextCourseKey = courses ? getDefaultCourseKey(effectiveSubjectKey) : '';
    if (!courses && filters.courseKey) {
      setFilters((prev) => ({ ...prev, courseKey: '' }));
    } else if (courses && !courses[filters.courseKey]) {
      setFilters((prev) => ({ ...prev, courseKey: nextCourseKey }));
    }
  }, [filters.subjectKey, filters.subjectForQuestionsKey, filters.courseKey, mode]);

  // Update standard set when subject changes
  useEffect(() => {
    // In addQuestion mode, use subjectForQuestionsKey as the effective subject
    const effectiveSubjectKey = determineEffectiveSubjectKey(filters, mode);
    const override = SUBJECT_MAP[effectiveSubjectKey]?.setOverride;

    if (override && filters.standardSetKey !== override) {
      setFilters((prev) => ({ ...prev, standardSetKey: override }));
    } else if (!override && !STATE_STANDARD_SETS[filters.standardSetKey]) {
      // When switching to a non-override subject and current standardSetKey is not a state,
      // restore user's cached state selection
      const userState = getStandardSet(diffitUser) || '';
      setFilters((prev) => ({ ...prev, standardSetKey: userState }));
    }
  }, [filters.subjectKey, filters.subjectForQuestionsKey, diffitUser, mode]);

  // Handle subject for questions restrictions via centralized constraint
  useEffect(() => {
    if (mode !== 'addQuestion') return;

    // Need to check the actual user's state, not the potentially overridden standardSetKey
    const userState = getStandardSet(diffitUser) || '';
    const adjusted = constrainSubjectForQuestions(filters.subjectForQuestionsKey, {
      userStateKey: userState,
      questionType: filters.questionType,
    });
    if (adjusted !== filters.subjectForQuestionsKey) {
      setFilters((prev) => ({ ...prev, subjectForQuestionsKey: adjusted }));
      skillsStorage.setSubjectForQuestions(adjusted);
    }
  }, [
    mode,
    filters.subjectForQuestionsKey,
    filters.standardSetKey,
    filters.questionType,
    diffitUser,
  ]);

  // Actions for updating filters and state
  const actions = {
    setSubject: (key) => {
      setFilters((prev) => ({ ...prev, subjectKey: key }));
      skillsStorage.setSubject(key);
    },
    setGrade: (key) => {
      setFilters((prev) => ({ ...prev, gradeKey: key }));
      skillsStorage.setGradeLevel(key);
    },
    setCourse: (key) => {
      setFilters((prev) => ({ ...prev, courseKey: key }));
      skillsStorage.setCourse(key);
    },
    setStandardSet: (key) => {
      setFilters((prev) => ({ ...prev, standardSetKey: key }));
      skillsStorage.setStandardSet(key);
    },
    setSubjectForQuestions: (key) => {
      setFilters((prev) => ({ ...prev, subjectForQuestionsKey: key }));
      skillsStorage.setSubjectForQuestions(key);
      // Reset selected skills for custom questions
      if (key === 'CUSTOM' && hasPremium) {
        setSelectedLanguageStandards([]);
        setSelectedContentStandards([]);
      }
    },
    setSearchQuery: (query) => {
      setFilters((prev) => ({ ...prev, searchQuery: query }));
    },
    setSelectedSkills: (skills) => {
      // Only accept skill objects with valid IDs
      const skillObjects = skills.filter((skill) => skill && skill.id);

      // Determine if we're in content or language mode
      const effectiveSubjectKey =
        mode === 'addQuestion' && filters.subjectForQuestionsKey
          ? filters.subjectForQuestionsKey
          : filters.subjectKey;
      const standardType = SUBJECT_MAP[effectiveSubjectKey]?.standardType || 'language';

      if (standardType === 'content') {
        // Content mode: multiple selection (respects maxSelectable)
        setSelectedContentStandards(skillObjects.slice(0, maxSelectable));
      } else {
        // Language mode: multiple selection (respects maxSelectable)
        setSelectedLanguageStandards(skillObjects.slice(0, maxSelectable));
      }
    },
    setFocusText: (skillId, focusText) => {
      // Update focus on standards
      const effectiveSubjectKey =
        mode === 'addQuestion' && filters.subjectForQuestionsKey
          ? filters.subjectForQuestionsKey
          : filters.subjectKey;
      const standardType = SUBJECT_MAP[effectiveSubjectKey]?.standardType || 'language';

      if (standardType === 'language') {
        setSelectedLanguageStandards((prev) =>
          prev.map((skill) => (skill.id === skillId ? { ...skill, focus: focusText } : skill))
        );
      } else {
        setSelectedContentStandards((prev) =>
          prev.map((skill) => (skill.id === skillId ? { ...skill, focus: focusText } : skill))
        );
      }
    },
    // Direct setters for clearing standards regardless of current mode
    setSelectedContentStandards: (standards) => {
      setSelectedContentStandards(standards);
    },
    setSelectedLanguageStandards: (standards) => {
      setSelectedLanguageStandards(standards);
    },
  };

  // Filter skills based on search query and grade
  const getFilteredSkills = useCallback(() => {
    if (!filters.standardSetKey) return [];

    let filtered = Array.isArray(skills) ? skills : [];

    // Filter by search query
    const query = filters.searchQuery.toLowerCase();
    if (query) {
      filtered = filtered.filter(
        (skill) =>
          skill.name?.toLowerCase().includes(query) ||
          skill.framework?.toLowerCase().includes(query) ||
          skill.description?.toLowerCase().includes(query) ||
          skill.strand?.toLowerCase().includes(query)
      );
    }

    // Pin initially selected skills (that are still selected) to the top via comparator
    if (initiallySelectedIds.size > 0) {
      let withPinnedFirst = filtered;
      if (!SUBJECT_MAP[filters.subjectKey]?.skipSorting) {
        withPinnedFirst = withPinnedFirst.slice().sort((a, b) => {
          const aPinned = initiallySelectedIds.has(a.id) && selectedSkillIds.includes(a.id);
          const bPinned = initiallySelectedIds.has(b.id) && selectedSkillIds.includes(b.id);
          return (bPinned ? 1 : 0) - (aPinned ? 1 : 0);
        });
      }
      return withPinnedFirst;
    }

    return filtered;
  }, [skills, filters.searchQuery, filters.standardSetKey, selectedSkillIds, initiallySelectedIds]);

  // Computed values
  const effectiveSubjectKey = determineEffectiveSubjectKey(filters, mode);
  const isContentStandard = SUBJECT_MAP[effectiveSubjectKey]?.standardType === 'content';

  // Check if user has selected a state (not whether current standardSetKey is a state)
  // If the subject has an override, we need to check the user's saved state instead
  const getUsersStateSelection = () => {
    // If current subject doesn't have an override, standardSetKey is the user's state
    if (!SUBJECT_MAP[effectiveSubjectKey]?.setOverride) {
      return filters.standardSetKey;
    }
    // Otherwise, get the user's saved state from localStorage
    return getStandardSet(diffitUser) || '';
  };

  const userStateSelection = getUsersStateSelection();
  const isStateSetSelected = !!userStateSelection && !!STATE_STANDARD_SETS[userStateSelection];
  // Allow override via initialFilters.maxSelectable, otherwise use defaults
  const defaultMaxSelectable = isContentStandard ? 1 : mode === 'packetSkills' ? 3 : 1;
  const maxSelectable = initialFilters.maxSelectable ?? defaultMaxSelectable;
  const filteredSkills = getFilteredSkills();

  return {
    // State
    filters,
    selectedLanguageStandards,
    selectedContentStandards,
    skills: filteredSkills,
    isLoading,
    error,

    // Computed values
    effectiveSubjectKey,
    isContentStandard,
    isStateSetSelected,
    maxSelectable,

    // Actions
    actions,

    // Constants
    COMPREHENSION_SKILL,
  };
};
